<template>
	<p
		v-if="isVisible"
		class="scrollbar-none flex flex-nowrap space-x-xs overflow-auto text-sm tracking-xl whitespace-nowrap text-[#5A5454]"
	>
		<template v-for="(crumb, index) in crumbs" :key="crumb.url">
			<span v-if="index > 0"> / </span>

			<nuxt-link
				:to="crumb.url"
				:title="crumb.title"
				:aria-label="crumb.title"
				class="hover:underline"
			>
				{{ crumb.title }}
			</nuxt-link>
		</template>
	</p>

	<v-placeholder
		v-else
		class="block h-6 rounded bg-gray-50 before:via-gray-100"
	/>
</template>

<script setup lang="ts">
import type {TreeItem} from "@pkg/shared/server/api/navigation.get";
import type {SimpleProductDataFragment} from "@pkg/shared/graphql";

import {useNavigationData} from "@pkg/shared/composables/use-navigation-data";
import {
	useStoryblokLinks,
	type StoryblokLink,
} from "@pkg/shared/composables/use-storyblok-links";

interface Crumb {
	url: string;
	title: string;
}

const properties = withDefaults(
	defineProps<{
		type?: "page" | "product" | "profile";
		product?: SimpleProductDataFragment;
	}>(),
	{
		type: "page",
		product: undefined,
	},
);

const config = useRuntimeConfig();

const route = useRoute();

const {t} = useI18n({
	useScope: "global",
});

// Only needed if this is a regular SB page
const links =
	properties.type === "page"
		? useStoryblokLinks().data
		: ref<StoryblokLink[]>([]);

// Only needed if this is a product page
const navigation =
	properties.type === "product" ? await useNavigationData() : [];

const isVisible = computed(
	() =>
		(properties.type === "product" && navigation.length > 0) ||
		(properties.type === "page" && links.value && links.value.length > 0) ||
		properties.type === "profile",
);

// Breadth-first traversal, looking for the first menu node that matches the given category. Result is the path of the node.
function findCategoryMenuPath(
	root: TreeItem,
	targetCategoryId: string,
): TreeItem[] | undefined {
	const queue: Array<{node: TreeItem; parentPath: TreeItem[]}> = [
		{node: root, parentPath: []},
	];

	while (queue.length > 0) {
		const {node, parentPath} = queue.shift()!;

		if ("categoryId" in node && node.categoryId === targetCategoryId) {
			return [...parentPath, node];
		}

		if (node.children) {
			for (const child of node.children) {
				queue.push({
					node: child,
					parentPath: [...parentPath, node],
				});
			}
		}
	}

	return undefined;
}

const crumbs = computed<Crumb[]>(() => {
	// If product page, the crumbs should be the "de facto" path to the product, ie the menu item path that matches the product's main category.
	if (properties.type === "product") {
		const mainCategory = properties.product?.mainCategory;

		// Look for a menu item (and its path) that matches the product's main category or one of the parent categories of that one
		let productCategory = mainCategory
			? properties.product.categories.find((x) => x.id === mainCategory.id)
			: undefined;
		let categoryMenuPath: TreeItem[] | undefined;
		// Loop while we have categories to find a match for
		while (productCategory) {
			const roomsRoot = navigation.find((x) => x.name === t("pages.rooms"));
			const productsRoot = navigation.find(
				(x) => x.type === t("pages.products"),
			);

			// Menu category pages can be associated to a certain products category. First try to find a matching menu item under the "rooms" top-level menu tree, then look for a fallback under the "products" tree.
			if (roomsRoot) {
				categoryMenuPath = findCategoryMenuPath(roomsRoot, productCategory.id);
			} else if (!categoryMenuPath && productsRoot) {
				categoryMenuPath = findCategoryMenuPath(
					productsRoot,
					productCategory.id,
				);
			}

			if (categoryMenuPath) {
				break;
			}

			const parentCategory = productCategory.parent;
			productCategory = parentCategory
				? properties.product?.categories.find((x) => x.id === parentCategory.id)
				: undefined;
		}

		// If we have no menu item that matches the product's main category, we can't show a very semantic breadcrumb for this page. Just show a simple one instead.
		if (!categoryMenuPath) {
			return [
				{
					url: `/${t("storyblok.products")}`,
					title: t("pages.products"),
				},
				{
					url: route.path,
					title: properties.product?.name || "",
				},
			];
		}

		// If we found a matching menu item, use its path as the breadcrumbs.

		// Remove the first "rooms" part of the path since it's not that useful as a lander page to users and otherwise the crumbs may get so long they don't fit well on mobile screens.
		categoryMenuPath.shift();

		return categoryMenuPath.map((item) => ({
			url: item.url || "",
			title: item.name,
		}));
	}

	// Static
	if (properties.type === "profile") {
		return [
			{
				url: `/${t("storyblok.home")}`,
				title: t("pages.home"),
			},
			{
				url: route.path,
				title: t("pages.profile"),
			},
		];
	}

	// Otherwise we have a SB page. Form breadcrumbs by comparing our URL against the full listing of stories ("links") we have in our SB space.

	const slugs = route.path.replace(/^\//, "").replace(/\/$/, "").split("/");

	const pages = slugs.map((_slug, index) =>
		slugs.slice(0, index + 1).join("/"),
	);

	return [
		{
			url: `/${t("storyblok.home")}`,
			title: t("pages.home"),
		},
		...(links.value ?? [])
			.filter((link) => pages.includes(link.slug))
			.map((link) => ({
				url: link.real_path,
				title: link.name,
			}))
			.sort((a, b) => a.url.length - b.url.length),
	];
});

useJsonld({
	"@context": "https://schema.org",
	"@type": "BreadcrumbList",
	itemListElement: crumbs.value.map((crumb, index) => ({
		"@type": "ListItem",
		position: index + 1,
		name: crumb.title,
		item: `${config.public.appUrl}${crumb.url}`,
	})),
});
</script>
